import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core'
import { AdminClient, CrmAssignee, LeadStatus } from '@parkupp/core'
import { ApexAxisChartSeries } from 'ng-apexcharts'
import { GlobalVariablesService } from 'src/app/services/global-variables.service'

interface SummaryItem {
    totalBays: number
    totalLeads: number
    totalValue: number
}

interface Summary {
    [key: string]: SummaryItem
}

interface OrderedSummaryItem {
    key: string
    displayName: string
    badgeColor?: string
    value: SummaryItem
}

interface BlockChart {
    series: ApexAxisChartSeries
    categories: string[]
    colors?: string[]
}

type MetricTab = 'leads' | 'value' | 'bays'
type BlockKey = 'source' | 'status' | 'location' | 'assignee' | 'campaign' | 'closedReason'

interface AgingBucket {
    label: string
    count: number
    color: string
}

const STATUS_COLORS: { [key: string]: string } = {
    [LeadStatus.NEW]: '#6b7280',
    [LeadStatus.CONTACT_ATTEMPTED]: '#94a3b8',
    [LeadStatus.CONTACTED]: '#3e8bff',
    [LeadStatus.PROSPECTING]: '#818cf8',
    [LeadStatus.NEGOTIATING]: '#a78bfa',
    [LeadStatus.CONVERTED]: '#4bd181',
    [LeadStatus.CLOSED]: '#ef4444',
}

const LOCATION_COLORS: { [key: string]: string } = {
    'Cape Town': '#3e8bff',
    'Johannesburg': '#f59e0b',
    'Pretoria': '#4bd181',
    'Durban': '#ef4444',
    'Gqeberha': '#a78bfa',
    'Bloemfontein': '#14b8a6',
    'East London': '#f97316',
    'Nelspruit': '#6366f1',
    'Kimberley': '#ec4899',
    'Polokwane': '#84cc16',
    'Rustenburg': '#06b6d4',
    'Other': '#6b7280',
}

const CLOSED_REASON_COLORS: { [key: string]: string } = {
    'No Availability in Area': '#ef4444',
    'No Response': '#f97316',
    'Duplicate': '#6b7280',
    'Budget Constraints': '#f59e0b',
    'Timing Issues': '#a78bfa',
    'Chose Competitor': '#ec4899',
    'Other': '#94a3b8',
}

const CHART_PALETTE = ['#3e8bff', '#4bd181', '#f59e0b', '#ef4444', '#a78bfa', '#14b8a6', '#f97316', '#6366f1', '#ec4899', '#84cc16', '#06b6d4', '#d946ef', '#0ea5e9', '#eab308', '#64748b']

function stableColorForString(str: string, palette: string[]): string {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
        hash = hash & hash
    }
    return palette[Math.abs(hash) % palette.length]
}

@Component({
    selector: 'app-parker-stats',
    templateUrl: './parker-stats.component.html',
    styleUrls: ['./parker-stats.component.scss'],
})
export class ParkerStatsComponent implements OnInit, OnChanges {
    @Input() filteredAdminClients: AdminClient[] = []
    @Input() crmAssigneeMap: { [key: string]: CrmAssignee } = {}

    leadStatusSummary: Summary = {}
    broadLocationSummary: Summary = {}
    closedReasonSummary: Summary = {}
    userResponsibleSummary: Summary = {}
    campaignSummary: Summary = {}
    sourceSummary: Summary = {}

    activeTabs: { [key in BlockKey]: MetricTab } = {
        source: 'leads',
        status: 'leads',
        location: 'leads',
        assignee: 'leads',
        campaign: 'leads',
        closedReason: 'leads',
    }

    sourceChart: BlockChart = { series: [], categories: [] }
    statusChart: BlockChart = { series: [], categories: [] }
    locationChart: BlockChart = { series: [], categories: [] }
    assigneeChart: BlockChart = { series: [], categories: [] }
    campaignChart: BlockChart = { series: [], categories: [] }
    closedReasonChart: BlockChart = { series: [], categories: [] }

    agingBuckets: AgingBucket[] = []
    agingChart: BlockChart = { series: [], categories: [] }

    readonly chartConfig = {
        type: 'bar' as const,
        height: 280,
        toolbar: { show: false },
    }
    readonly plotOptions = {
        bar: { horizontal: true, barHeight: '60%', borderRadius: 4 },
    }
    readonly dataLabels = {
        enabled: true,
        style: { fontSize: '12px', fontWeight: 600 },
    }
    readonly defaultColors = ['#3e8bff']

    private readonly ACTIVE_PIPELINE_STATUSES = [
        LeadStatus.NEW,
        LeadStatus.CONTACT_ATTEMPTED,
        LeadStatus.CONTACTED,
        LeadStatus.PROSPECTING,
        LeadStatus.NEGOTIATING,
    ]

    constructor(private globalVariablesService: GlobalVariablesService) {}

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['filteredAdminClients']) {
            this.summarizeData()
        }
    }

    summarizeData() {
        if (!this.filteredAdminClients) return
        this.leadStatusSummary = this.groupBy(this.filteredAdminClients, 'leadStatus')
        this.broadLocationSummary = this.groupBy(this.filteredAdminClients, 'broadLocation')
        this.closedReasonSummary = this.groupBy(this.filteredAdminClients, 'leadClosedReason')
        this.userResponsibleSummary = this.groupBy(this.filteredAdminClients, 'userResponsible')
        this.campaignSummary = this.groupBy(this.filteredAdminClients, 'p_campaign')
        this.sourceSummary = this.groupBy(this.filteredAdminClients, 'p_source')

        this.buildAllCharts()
        this.buildAgingChart()
    }

    // --- Main summary card getters ---

    getTotalLeads(): number {
        return this.filteredAdminClients?.length || 0
    }

    getTotalBays(): number {
        return this.filteredAdminClients?.reduce((sum, c) => sum + (c.numberOfBays || 0), 0) || 0
    }

    getConvertedLeads(): number {
        return this.leadStatusSummary[LeadStatus.CONVERTED]?.totalLeads || 0
    }

    getConversionRate(): number {
        const total = this.getTotalLeads()
        if (total === 0) return 0
        return (this.getConvertedLeads() / total) * 100
    }

    getActivePipeline(): number {
        return this.ACTIVE_PIPELINE_STATUSES.reduce((sum, s) => sum + (this.leadStatusSummary[s]?.totalLeads || 0), 0)
    }

    getAvgDealSize(): number {
        const converted = this.getConvertedLeads()
        if (converted === 0) return 0
        return (this.leadStatusSummary[LeadStatus.CONVERTED]?.totalBays || 0) / converted
    }

    // --- Secondary stat card getters ---

    getNewSubscriptions(): number {
        if (!this.filteredAdminClients) return 0
        return this.filteredAdminClients.filter((c) => {
            const subs = (c as any).subscriptionSummaries
            if (!subs) return false
            const subEntries = Object.values(subs) as any[]
            if (subEntries.length !== 1) return false
            return subEntries[0].status === 'Active' || subEntries[0].status === 'Completed'
        }).length
    }

    getClosedLeads(): number {
        return this.leadStatusSummary[LeadStatus.CLOSED]?.totalLeads || 0
    }

    getTotalValue(): number {
        if (!this.filteredAdminClients) return 0
        return this.filteredAdminClients.reduce((sum, c) => {
            return sum + this.globalVariablesService.calculateLeadValue(c.numberOfBays || 0, c.monthsRequired || 0)
        }, 0)
    }

    getNotAssignedLeads(): number {
        return this.filteredAdminClients?.filter((c) => !c.userResponsible).length || 0
    }

    getNewStatusLeads(): number {
        return this.leadStatusSummary[LeadStatus.NEW]?.totalLeads || 0
    }

    getRegisteredUsers(): number {
        return this.filteredAdminClients?.filter((c) => !c.noAuth).length || 0
    }

    getAgingLeads(): number {
        const twoWeeksAgo = new Date()
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14)
        return (
            this.filteredAdminClients?.filter((c) => {
                if (!this.ACTIVE_PIPELINE_STATUSES.includes(c.leadStatus as LeadStatus)) return false
                if (!c.createdAt) return false
                const d = c.createdAt.toDate ? c.createdAt.toDate() : new Date(c.createdAt as any)
                return d < twoWeeksAgo
            }).length || 0
        )
    }

    // --- Aging chart ---

    private buildAgingChart(): void {
        if (!this.filteredAdminClients) return
        const now = new Date()
        const buckets = { fresh: 0, oneWeek: 0, twoWeeks: 0, oneMonth: 0, ancient: 0 }

        this.filteredAdminClients.forEach((c) => {
            if (!this.ACTIVE_PIPELINE_STATUSES.includes(c.leadStatus as LeadStatus)) return
            if (!c.createdAt) return
            const d = c.createdAt.toDate ? c.createdAt.toDate() : new Date(c.createdAt as any)
            const daysOld = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24))

            if (daysOld > 30) {
                buckets.ancient++
            } else if (daysOld > 14) {
                buckets.oneMonth++
            } else if (daysOld > 7) {
                buckets.twoWeeks++
            } else if (daysOld > 3) {
                buckets.oneWeek++
            } else {
                buckets.fresh++
            }
        })

        this.agingBuckets = [
            { label: '< 3 days', count: buckets.fresh, color: '#4bd181' },
            { label: '3 - 7 days', count: buckets.oneWeek, color: '#3e8bff' },
            { label: '1 - 2 weeks', count: buckets.twoWeeks, color: '#f59e0b' },
            { label: '2 wks - 1 mo', count: buckets.oneMonth, color: '#ef4444' },
            { label: '> 1 month', count: buckets.ancient, color: '#7f1d1d' },
        ]

        this.agingChart = {
            series: [{ name: 'Leads', data: this.agingBuckets.map((b) => b.count) }],
            categories: this.agingBuckets.map((b) => b.label),
            colors: this.agingBuckets.map((b) => b.color),
        }
    }

    // --- Tab switching & chart building ---

    switchTab(block: BlockKey, tab: MetricTab): void {
        this.activeTabs[block] = tab
        this.buildBlockChart(block)
    }

    private buildAllCharts(): void {
        const blocks: BlockKey[] = ['source', 'status', 'location', 'assignee', 'campaign', 'closedReason']
        blocks.forEach((b) => this.buildBlockChart(b))
    }

    private buildBlockChart(block: BlockKey): void {
        const tab = this.activeTabs[block]
        let sorted: OrderedSummaryItem[]

        switch (block) {
            case 'source':
                sorted = this.getSortedSource()
                break
            case 'status':
                sorted = this.getOrderedLeadStatusSummary()
                break
            case 'location':
                sorted = this.getSortedBroadLocation()
                break
            case 'assignee':
                sorted = this.getSortedUserResponsible()
                break
            case 'campaign':
                sorted = this.getSortedCampaign()
                break
            case 'closedReason':
                sorted = this.getSortedClosedReason()
                break
        }

        const filtered = sorted.filter((s) => s.key !== '0')
        const categories = filtered.map((s) => s.displayName)
        const data = filtered.map((s) => {
            switch (tab) {
                case 'leads':
                    return s.value.totalLeads
                case 'bays':
                    return s.value.totalBays
                case 'value':
                    return Math.round(s.value.totalValue)
            }
        })

        const seriesName = tab === 'leads' ? 'Leads' : tab === 'bays' ? 'Bays' : 'Value (R)'

        const chart: BlockChart = {
            series: [{ name: seriesName, data }],
            categories,
        }

        switch (block) {
            case 'status':
                chart.colors = filtered.map((s) => STATUS_COLORS[s.key] || '#6b7280')
                break
            case 'assignee':
                chart.colors = filtered.map((s) => s.badgeColor || '#6c757d')
                break
            case 'location':
                chart.colors = filtered.map((s) => LOCATION_COLORS[s.key] || stableColorForString(s.key, CHART_PALETTE))
                break
            case 'closedReason':
                chart.colors = filtered.map((s) => CLOSED_REASON_COLORS[s.key] || stableColorForString(s.key, CHART_PALETTE))
                break
            case 'source':
            case 'campaign':
                chart.colors = filtered.map((s) => stableColorForString(s.key, CHART_PALETTE))
                break
        }

        switch (block) {
            case 'source':
                this.sourceChart = chart
                break
            case 'status':
                this.statusChart = chart
                break
            case 'location':
                this.locationChart = chart
                break
            case 'assignee':
                this.assigneeChart = chart
                break
            case 'campaign':
                this.campaignChart = chart
                break
            case 'closedReason':
                this.closedReasonChart = chart
                break
        }
    }

    // --- Data grouping ---

    private groupBy(array: AdminClient[], key: string): Summary {
        return array.reduce((result: Summary, client: any) => {
            const keyValue = client[key] || 0
            if (!result[keyValue]) {
                result[keyValue] = { totalBays: 0, totalLeads: 0, totalValue: 0 }
            }
            result[keyValue].totalBays += client.numberOfBays || 0
            result[keyValue].totalLeads += 1
            result[keyValue].totalValue += this.globalVariablesService.calculateLeadValue(client.numberOfBays || 0, client.monthsRequired || 0)
            return result
        }, {} as Summary)
    }

    // --- Sorting helpers ---

    private sortByLeadsDesc(summary: Summary): OrderedSummaryItem[] {
        return Object.entries(summary)
            .map(([key, value]) => ({ key, displayName: key, value }))
            .sort((a, b) => b.value.totalLeads - a.value.totalLeads)
    }

    getOrderedLeadStatusSummary(): OrderedSummaryItem[] {
        const orderedStatuses = [LeadStatus.NEW, LeadStatus.CONTACT_ATTEMPTED, LeadStatus.CONTACTED, LeadStatus.PROSPECTING, LeadStatus.NEGOTIATING, LeadStatus.CONVERTED, LeadStatus.CLOSED]
        return orderedStatuses
            .filter((status) => this.leadStatusSummary[status])
            .map((status) => ({
                key: status,
                displayName: status,
                value: this.leadStatusSummary[status],
            }))
    }

    getSortedUserResponsible(): OrderedSummaryItem[] {
        return Object.entries(this.userResponsibleSummary)
            .filter(([key]) => key === '0' || this.crmAssigneeMap[key])
            .map(([key, value]) => {
                const assignee = this.crmAssigneeMap[key]
                return {
                    key,
                    displayName: assignee?.displayName || 'Not Assigned',
                    badgeColor: assignee?.badgeColor,
                    value,
                }
            })
            .sort((a, b) => b.value.totalLeads - a.value.totalLeads)
    }

    getSortedBroadLocation(): OrderedSummaryItem[] {
        return this.sortByLeadsDesc(this.broadLocationSummary)
    }

    getSortedSource(): OrderedSummaryItem[] {
        return this.sortByLeadsDesc(this.sourceSummary)
    }

    getSortedCampaign(): OrderedSummaryItem[] {
        return this.sortByLeadsDesc(this.campaignSummary)
    }

    getSortedClosedReason(): OrderedSummaryItem[] {
        return this.sortByLeadsDesc(this.closedReasonSummary)
    }
}
